/**
 * CardsPreview — read-only pass of the script in the draft card order, so the
 * writer can read the new flow before committing it to the actual script.
 */
import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useIsMobile } from "@/lib/useMediaQuery";
import { BookOpen, X, Check, Loader2 } from "lucide-react";

export function CardsPreview({ scenes, order, moved, readOnly, committing, onCommit, onClose }) {
  const isMobile = useIsMobile();
  const byId = useMemo(() => Object.fromEntries(scenes.map((s) => [s.sceneId, s])), [scenes]);
  const ordered = useMemo(() => order.map((sid) => byId[sid]).filter(Boolean), [order, byId]);

  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[80] flex flex-col bg-[#efe9df]" data-testid="cards-preview">
      <div className="shrink-0 bg-[#2d2a26] text-white shadow-md relative z-10" data-testid="cards-preview-banner">
        <div className="max-w-[8.5in] mx-auto px-4 py-2.5 flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-2 font-display text-base shrink-0">
            <BookOpen size={15} className="text-[var(--brand)]" /> Preview — draft scene order
          </span>
          <span className="text-[11px] text-white/60" data-testid="cards-preview-counts">
            {ordered.length} scenes · {moved} position{moved === 1 ? "" : "s"} changed · the script is untouched until you commit
          </span>
          <div className="flex items-center gap-2 ml-auto shrink-0">
            {!readOnly && (
              <button onClick={onCommit} disabled={committing} className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-md btn-brand disabled:opacity-50" data-testid="cards-preview-commit">
                {committing ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Commit this order
              </button>
            )}
            <button onClick={onClose} className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md bg-white/10 hover:bg-white/20 transition-colors" data-testid="cards-preview-close">
              <X size={13} /> Back to board
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-6 px-3">
        <div className="screenplay-canvas-wrap">
          <div className={`screenplay-page ${isMobile ? "mobile-format" : ""}`} style={{ minHeight: "auto", cursor: "default", paddingTop: "0.6in", paddingBottom: "0.8in" }}>
            {ordered.map((sc, i) => (
              <div key={sc.sceneId} className="relative mb-2" data-testid={`cards-preview-scene-${i + 1}`}>
                <div className="flex items-center gap-2 text-[10px] font-sans uppercase tracking-widest text-[var(--ink-muted)] select-none">
                  <span>Sc {i + 1}</span>
                  {sc.number !== i + 1 && (
                    <span className="text-[var(--brand)] font-bold" data-testid={`cards-preview-moved-${i + 1}`}>moved — was Sc {sc.number}</span>
                  )}
                </div>
                {sc.els.map((el) => (
                  <div key={el.id} className={`script-line el-${el.type}`}>{el.text}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
